import { createContext, useContext, useState, useEffect } from "react"
import { supabase } from "../supabaseClient"

const AuthContext = createContext({})

export const useAuth = () => useContext(AuthContext)

// Hierarquia de papeis (maior numero = mais permissoes)
const PAPEL_HIERARQUIA = {
  admin: 100,
  diretor_geral: 50,
  diretor_pedagogico: 40,
  diretor_administrativo: 40,
  secretaria: 20,
  secretaria_2: 10,
}

// Definicao de permissoes por papel
const PERMISSOES = {
  admin: [
    "instituicoes.ver", "instituicoes.criar", "instituicoes.editar", "instituicoes.excluir",
    "usuarios.ver", "usuarios.criar", "usuarios.editar", "usuarios.excluir",
    "cursos.ver", "cursos.criar", "cursos.editar", "cursos.excluir",
    "departamentos.ver", "departamentos.criar", "departamentos.editar", "departamentos.excluir",
    "professores.ver", "professores.criar", "professores.editar", "professores.excluir",
    "disciplinas.ver", "disciplinas.criar", "disciplinas.editar", "disciplinas.excluir",
    "turmas.ver", "turmas.criar", "turmas.editar", "turmas.excluir",
    "salas.ver", "salas.criar", "salas.editar", "salas.excluir",
    "horarios.ver", "horarios.criar", "horarios.editar", "horarios.excluir",
    "configuracoes.ver", "configuracoes.editar",
    "dashboard.ver",
  ],
  diretor_geral: [
    "usuarios.ver", "usuarios.criar", "usuarios.editar",
    "cursos.ver", "cursos.criar", "cursos.editar", "cursos.excluir",
    "departamentos.ver", "departamentos.criar", "departamentos.editar", "departamentos.excluir",
    "professores.ver", "professores.criar", "professores.editar", "professores.excluir",
    "disciplinas.ver", "disciplinas.criar", "disciplinas.editar", "disciplinas.excluir",
    "turmas.ver", "turmas.criar", "turmas.editar", "turmas.excluir",
    "salas.ver", "salas.criar", "salas.editar", "salas.excluir",
    "horarios.ver", "horarios.criar", "horarios.editar", "horarios.excluir",
    "configuracoes.ver", "configuracoes.editar",
    "dashboard.ver",
  ],
  diretor_pedagogico: [
    "cursos.ver",
    "departamentos.ver",
    "professores.ver", "professores.criar", "professores.editar",
    "disciplinas.ver", "disciplinas.criar", "disciplinas.editar",
    "turmas.ver", "turmas.criar", "turmas.editar",
    "salas.ver",
    "horarios.ver", "horarios.criar", "horarios.editar",
    "dashboard.ver",
  ],
  diretor_administrativo: [
    "cursos.ver",
    "departamentos.ver",
    "professores.ver",
    "disciplinas.ver",
    "turmas.ver",
    "salas.ver", "salas.criar", "salas.editar", "salas.excluir",
    "horarios.ver",
    "dashboard.ver",
  ],
  secretaria: [
    "cursos.ver",
    "departamentos.ver",
    "professores.ver",
    "disciplinas.ver",
    "turmas.ver", "turmas.criar", "turmas.editar",
    "salas.ver",
    "horarios.ver", "horarios.criar", "horarios.editar",
    "dashboard.ver",
  ],
  secretaria_2: [
    "cursos.ver",
    "departamentos.ver",
    "professores.ver",
    "disciplinas.ver",
    "turmas.ver",
    "salas.ver",
    "horarios.ver",
    "dashboard.ver",
  ],
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const loadUser = async () => {
      const userId = localStorage.getItem("userId")
      const userName = localStorage.getItem("userName")
      const userEmail = localStorage.getItem("userEmail")
      const userPapel = localStorage.getItem("userPapel")
      const instituicaoId = localStorage.getItem("instituicaoId")

      if (userId && userEmail) {
        // Verificar no banco se o usuário ainda está ativo
        try {
          const { data: dbUser, error: dbError } = await supabase
            .from("usuarios")
            .select("id, nome, email, papel, instituicao_id, ativo")
            .eq("id", userId)
            .maybeSingle()

          if (dbError) {
            console.error("Erro ao verificar usuario no DB:", dbError)
          }

          if (dbUser) {
            if (dbUser.ativo === false) {
              // Limpar sessão local e avisar que o usuário foi bloqueado
              localStorage.removeItem("userId")
              localStorage.removeItem("userName")
              localStorage.removeItem("userEmail")
              localStorage.removeItem("userPapel")
              localStorage.removeItem("instituicaoId")
              setUser(null)
              setUserProfile(null)
              window.dispatchEvent(new CustomEvent("auth-blocked", { detail: "Usuário sem autorização." }))
              setLoading(false)
              return
            }

            setUser({ id: userId, email: userEmail })
            setUserProfile({
              id: userId,
              nome: dbUser.nome || userName,
              email: dbUser.email || userEmail,
              papel: dbUser.papel || userPapel,
              instituicao_id: dbUser.instituicao_id || instituicaoId || null,
            })
          } else {
            // Se não existir no DB, manter limpeza local
            setUser({ id: userId, email: userEmail })
            setUserProfile({
              id: userId,
              nome: userName,
              email: userEmail,
              papel: userPapel,
              instituicao_id: instituicaoId || null,
            })
          }
        } catch (err) {
          console.error("Erro loadUser:", err)
          setUser({ id: userId, email: userEmail })
          setUserProfile({
            id: userId,
            nome: userName,
            email: userEmail,
            papel: userPapel,
            instituicao_id: instituicaoId || null,
          })
        }
      }

      setLoading(false)
    }

    // Ouvir o evento de login para recarregar o usuário
    const handleLoginEvent = () => {
      loadUser()
    }

    // Inicializar carregamento do usuário
    loadUser()
    
    // Adicionar listener para evento de login
    window.addEventListener("auth-login", handleLoginEvent)
    
    return () => {
      window.removeEventListener("auth-login", handleLoginEvent)
    }
  }, [])

  const signOut = () => {
    localStorage.removeItem("userId")
    localStorage.removeItem("userName")
    localStorage.removeItem("userEmail")
    localStorage.removeItem("userPapel")
    localStorage.removeItem("instituicaoId")

    setUser(null)
    setUserProfile(null)
    window.dispatchEvent(new CustomEvent("auth-logout"))
  }

  // Verifica se o usuario tem um papel com nivel igual ou superior ao requerido
  const hasPermission = (requiredPapel) => {
    if (!userProfile) return false

    const userLevel = PAPEL_HIERARQUIA[userProfile.papel] || 0
    const requiredLevel = PAPEL_HIERARQUIA[requiredPapel] || 0

    return userLevel >= requiredLevel
  }

  // Verifica se o usuario tem uma permissao especifica
  const can = (permissao) => {
    if (!userProfile) return false
    
    const userPapel = userProfile.papel
    const permissoesDoPapel = PERMISSOES[userPapel] || []
    
    return permissoesDoPapel.includes(permissao)
  }

  // Verifica se pode ver uma determinada pagina/recurso
  const canAccess = (recurso) => {
    return can(`${recurso}.ver`)
  }

  // Verifica se pode criar em um determinado recurso
  const canCreate = (recurso) => {
    return can(`${recurso}.criar`)
  }

  // Verifica se pode editar em um determinado recurso
  const canEdit = (recurso) => {
    return can(`${recurso}.editar`)
  }

  // Verifica se pode excluir em um determinado recurso
  const canDelete = (recurso) => {
    return can(`${recurso}.excluir`)
  }

  const value = {
    user,
    userProfile,
    loading,
    signOut,
    hasPermission,
    can,
    canAccess,
    canCreate,
    canEdit,
    canDelete,
    isAdmin: userProfile?.papel === "admin",
    isDiretor: ["diretor_geral", "diretor_pedagogico", "diretor_administrativo"].includes(userProfile?.papel),
    isDiretorGeral: userProfile?.papel === "diretor_geral",
    instituicaoId: userProfile?.instituicao_id,
    papel: userProfile?.papel,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
